import { useMemo } from "react";
import { STATIC_NAV } from "@/components/layout/Sidebar";
import { useAuthStore } from "@/store/auth.store";
import { isSystemOwner } from "@/lib/authz";
import { TOUR_STEP_CONTENT } from "./tourContent";

export interface TourStep {
  path: string;
  title: string;
  rule: string;
  /** Seletor CSS do item real na sidebar — mesmo elemento que o usuário usa pra navegar. */
  targetSelector: string;
}

export function useTourSteps(): { steps: TourStep[]; shouldAutoStart: boolean } {
  const user = useAuthStore((state) => state.user);
  const isImpersonating = useAuthStore((state) => state.isImpersonating);
  // Selector escopado (não `usePermissions()`, que assina o auth store inteiro sem
  // seletor): useTourSteps roda dentro do MainLayout, ancestral do Topbar, e uma
  // assinatura de estado inteiro nesse nível transforma qualquer set() disparado
  // pelo Topbar num novo render de MainLayout -> Topbar -> novo set() -> loop.
  const permissions = useAuthStore((state) => state.user?.permissions ?? []);

  const steps = useMemo<TourStep[]>(() => {
    const can = (permission: string) => permissions.includes(permission);
    return STATIC_NAV
      .filter((item) =>
        (!item.permission || can(item.permission))
        && (!item.masterOnly || isSystemOwner(user))
        && (!item.hideForMaster || !isSystemOwner(user)),
      )
      .map((item) => {
        const content = TOUR_STEP_CONTENT[item.path];
        if (!content) return null;
        return {
          path: item.path,
          title: content.title,
          rule: content.rule,
          targetSelector: `a[href="${item.path}"]`,
        };
      })
      .filter((step): step is TourStep => step !== null);
  }, [user, permissions]);

  const shouldAutoStart = !isImpersonating && Boolean(user) && !user?.onboarding_tour_seen_at;

  return { steps, shouldAutoStart };
}
